import React, { Children, forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import classNames from 'classnames';
import { startCase } from 'lodash';
import { Box, assignInlineVars, tokens } from '@fiverr-private/theme';
import { Tooltip } from '@fiverr-private/tooltip';
import type { TabInternalProps, TabProps } from '../types';
import { useTabContext } from '../TabContext';
import { SEGMENTED_CONTROL, SSR_INDICATOR } from '../stories/constants';
import { isSegmentedControlVariant } from '../helpers';
import { getStylingProps } from './style';
import * as classes from './style.ve.css';
import { renderTabChild } from './helpers';

const Tab = forwardRef<HTMLButtonElement, TabProps>(
  (
    { isLink = false, isDisabled = false, onClick, children, className, tooltipContent, tooltipProps, ...props },
    ref
  ) => {
    const {
      panelId,
      placement = 'top',
      variant = 'default',
      isStretched = false,
      isSelected,
      id,
    } = props as TabInternalProps;
    const tabRef = useRef<HTMLButtonElement>(null);
    const { setFocusedTab, isIndicatorRendered, size } = useTabContext();
    const ssrIndicatorClassName = `${isSegmentedControlVariant(variant) ? SEGMENTED_CONTROL : variant}${SSR_INDICATOR}${
      variant === 'default' ? startCase(placement) : ''
    }`;

    useEffect(() => {
      if (isSelected) {
        setFocusedTab(tabRef);
      }
    }, [isSelected, setFocusedTab]);

    useImperativeHandle<HTMLButtonElement | null, HTMLButtonElement | null>(ref, () => tabRef.current);

    const tabContent = (
      <Box
        ref={tabRef}
        as="button"
        role="tab"
        id={id}
        aria-selected={isSelected ? 'true' : 'false'}
        aria-controls={panelId}
        aria-disabled={isDisabled ? undefined : 'false'}
        disabled={isDisabled}
        tabIndex={isSelected ? 0 : -1}
        data-link={isLink ? 'true' : undefined}
        onClick={onClick}
        zIndex="1"
        className={classNames(
          classes.defaultClass,
          {
            [classes[ssrIndicatorClassName]]: isSelected && !isIndicatorRendered,
            [classes.isLink]: isLink,
          },
          className
        )}
        style={assignInlineVars({
          [classes.LinkPadding]:
            isLink && (placement === 'top' || placement === 'bottom') && size !== 'sm'
              ? tokens.spacing[8]
              : tokens.spacing[4],
        })}
        {...getStylingProps(variant, placement, isLink, isDisabled, isStretched, isSelected, size)}
        {...props}
      >
        {Children.map(Children.toArray(children), renderTabChild({ isLink }))}
      </Box>
    );

    return tooltipContent ? (
      <Tooltip
        appendToBody
        content={tooltipContent}
        anchorProps={isStretched ? { display: 'flex', flex: 1 } : undefined}
        {...tooltipProps}
      >
        {tabContent}
      </Tooltip>
    ) : (
      tabContent
    );
  }
);

Tab.displayName = 'Tab';
Object.assign(Tab, { id: 'Tab' });

export default Tab;
